import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTasks, getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar'; // kept for potential future use
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import { CalendarDays, Filter, Loader2, X, Building2 } from 'lucide-react';
import { format } from 'date-fns';

const PIE_COLORS = ['#4338CA', '#7C3AED', '#A855F7', '#D946EF', '#EC4899', '#F97316'];
import { useDepartments } from '../hooks/useDepartments';
const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
const QUARTERS = [
  { label: 'Q1 (Jan-Mar)', value: 'Q1', months: [0, 1, 2] },
  { label: 'Q2 (Apr-Jun)', value: 'Q2', months: [3, 4, 5] },
  { label: 'Q3 (Jul-Sep)', value: 'Q3', months: [6, 7, 8] },
  { label: 'Q4 (Oct-Dec)', value: 'Q4', months: [9, 10, 11] },
];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ENTITIES = ['UOSPL', 'UAPL', 'UAFPL', 'United Group'];
const BRANDS = ['United Group', 'Unitile', 'UniVicoustic', 'U-Flex', 'Forza', 'UniPro'];
const STATUS_COLORS = {
  'To Do': '#94A3B8', 'In Progress': '#F59E0B', 'In Review': '#8B5CF6',
  'On Hold': '#64748B', 'Completed': '#10B981', 'Cancelled': '#EF4444',
};

export const AnalyticsPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const isAdmin = user?.role === 'Admin' || user?.role === 'Super Admin';
  const isHOD = user?.role === 'Admin';
  const myDepartments = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
  const isMultiDeptAdmin = isHOD && myDepartments.length > 1;
  const isPersonalOnly = user?.role === 'Coordinator' || user?.role === 'Member';

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  // Hierarchical date filter: Year → Month → Date
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth()); // -1 = all months
  const [filterDay, setFilterDay] = useState(0); // 0 = all days

  const isToday = (() => {
    const now = new Date();
    return filterYear === now.getFullYear() && filterMonth === now.getMonth() && filterDay === now.getDate();
  })();

  const daysInMonth = filterMonth >= 0 ? new Date(filterYear, filterMonth + 1, 0).getDate() : 0;

  const hasActiveFilters = selectedDept || selectedEmployee || selectedEntities.length > 0 || selectedBrands.length > 0 ||
    filterYear !== new Date().getFullYear() || filterMonth !== new Date().getMonth() || filterDay !== 0;

  const clearAllFilters = () => {
    setSelectedDept('');
    setSelectedEmployee('');
    setSelectedEntities([]);
    setSelectedBrands([]);
    setFilterYear(new Date().getFullYear());
    setFilterMonth(new Date().getMonth());
    setFilterDay(0);
  };

  const toggleEntity = (entity) => {
    setSelectedEntities(prev =>
      prev.includes(entity) ? prev.filter(e => e !== entity) : [...prev, entity]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isSuperAdmin && selectedDept) params.department = selectedDept;
      const res = await getTasks(params);
      setTasks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedDept, isSuperAdmin]);

  const fetchEmployees = useCallback(async () => {
    if (!isAdmin && !isSuperAdmin) return;
    try {
      const params = {};
      if (isSuperAdmin && selectedDept) params.department = selectedDept;
      else if (isHOD && !isMultiDeptAdmin) params.department = user?.department;
      const res = await getUsers(params);
      setEmployees(res.data.filter(u => u.role === 'Coordinator' || u.role === 'Member'));
    } catch { setEmployees([]); }
  }, [isAdmin, isSuperAdmin, isHOD, isMultiDeptAdmin, selectedDept, user?.department]);

  useEffect(() => { fetchTasks(); fetchEmployees(); }, [fetchTasks, fetchEmployees]);

  // Date range computation — hierarchy: Year → Month → Date
  const getDateRange = () => {
    const y = filterYear;
    if (filterMonth < 0) {
      // Year only
      return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
    }
    if (filterDay > 0) {
      // Specific date
      return { start: new Date(y, filterMonth, filterDay, 0, 0, 0, 0), end: new Date(y, filterMonth, filterDay, 23, 59, 59, 999) };
    }
    // Month
    const endM = new Date(y, filterMonth + 1, 0);
    endM.setHours(23, 59, 59, 999);
    return { start: new Date(y, filterMonth, 1), end: endM };
  };

  const getFilterLabel = () => {
    const y = filterYear;
    if (filterDay > 0 && filterMonth >= 0) {
      return format(new Date(y, filterMonth, filterDay), 'dd MMM yyyy');
    }
    if (filterMonth >= 0) {
      return `${MONTHS[filterMonth]} ${y}`;
    }
    return `${y}`;
  };

  // Apply all filters - use created_at for analytics (all tasks created in period)
  const { start, end } = getDateRange();
  let filteredTasks = tasks.filter(t => {
    const taskDate = new Date(t.created_at || t.due_date);
    return taskDate >= start && taskDate <= end;
  });

  // Role-based filtering
  if (isPersonalOnly) {
    filteredTasks = filteredTasks.filter(t => t.assigned_to === user?.id);
  } else if (isHOD && !isSuperAdmin) {
    filteredTasks = filteredTasks.filter(t => myDepartments.includes(t.department));
    if (selectedEmployee) {
      filteredTasks = filteredTasks.filter(t => t.assigned_to === selectedEmployee);
    }
  } else if (isSuperAdmin) {
    if (selectedDept) filteredTasks = filteredTasks.filter(t => t.department === selectedDept);
    if (selectedEmployee) filteredTasks = filteredTasks.filter(t => t.assigned_to === selectedEmployee);
  }

  // Entity filter (Admin/Super Admin)
  if (selectedEntities.length > 0 && (isAdmin || isSuperAdmin)) {
    filteredTasks = filteredTasks.filter(t => selectedEntities.includes(t.entity));
  }

  // Brand filter (Admin/Super Admin)
  if (selectedBrands.length > 0 && (isAdmin || isSuperAdmin)) {
    filteredTasks = filteredTasks.filter(t => selectedBrands.includes(t.brand));
  }

  // KPIs
  const totalTasks = filteredTasks.length;
  const completedTasks = filteredTasks.filter(t => t.status === 'Completed').length;
  const onTimeTasks = filteredTasks.filter(t => t.status === 'Completed' && new Date(t.due_date) >= new Date(t.updated_at || t.created_at)).length;
  const overdueTasks = filteredTasks.filter(t => {
    if (!['To Do','In Progress'].includes(t.status)) return false;
    const due = new Date(t.due_date);
    due.setHours(23, 59, 59, 999);
    return new Date() > due;
  }).length;
  const completionRate = totalTasks ? ((completedTasks / totalTasks) * 100).toFixed(1) : 0;
  const onTimeRate = completedTasks ? ((onTimeTasks / completedTasks) * 100).toFixed(1) : 0;
  const delayRate = totalTasks ? ((overdueTasks / totalTasks) * 100).toFixed(1) : 0;

  // Chart data
  // For Coordinator/Member, personal charts are scoped to their OWN assigned tasks
  const personalTasks = filteredTasks.filter(t => t.assigned_to === user?.id);
  const chartBase = isPersonalOnly ? personalTasks : filteredTasks;

  const statusMap = {};
  chartBase.forEach(t => { if (t.status) statusMap[t.status] = (statusMap[t.status] || 0) + 1; });
  const statusData = Object.entries(statusMap).map(([name, value]) => ({ name, value }));
  const showStatusChart = isPersonalOnly && statusData.length > 0;

  const categoryMap = {};
  filteredTasks.forEach(t => { const c = t.category || 'Uncategorized'; categoryMap[c] = (categoryMap[c] || 0) + 1; });
  const categoryData = Object.entries(categoryMap).map(([name, value]) => ({ name, value }));

  // Task Creation Trend (by month)
  const trendMap = {};
  filteredTasks.forEach(t => {
    const d = new Date(t.created_at || t.due_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    trendMap[key] = (trendMap[key] || 0) + 1;
  });
  const trendData = Object.entries(trendMap).sort().map(([month, count]) => ({ month, tasks: count }));

  // Individual vs Team work type chart
  const individualCount = filteredTasks.filter(t => (t.work_type || 'Individual') === 'Individual').length;
  const teamCount = filteredTasks.filter(t => t.work_type === 'Team').length;
  const workTypeData = [
    { name: 'Individual', value: individualCount },
    { name: 'Team', value: teamCount },
  ];

  // Tasks by Employee (only for Admin/Super Admin)
  const employeeMap = {};
  if (!isPersonalOnly) {
    filteredTasks.forEach(t => {
      const name = t.assigned_to_name || 'Unassigned';
      employeeMap[name] = (employeeMap[name] || 0) + 1;
    });
  }
  const employeeData = Object.entries(employeeMap).map(([name, value]) => ({ name, value }));

  // Workload distribution (To Do + In Progress per employee)
  // Member: only self; Coordinator/Admin: dept; Super Admin: all (or filtered dept)
  const workloadScopeTasks = (() => {
    if (user?.role === 'Member') return filteredTasks.filter(t => t.assigned_to === user?.id);
    return filteredTasks;
  })();
  const workloadMap = {};
  workloadScopeTasks.forEach(t => {
    const name = t.assigned_to_name || 'Unassigned';
    if (!workloadMap[name]) workloadMap[name] = { name, todo: 0, in_progress: 0 };
    if (t.status === 'To Do') workloadMap[name].todo += 1;
    else if (t.status === 'In Progress') workloadMap[name].in_progress += 1;
  });
  const workloadData = Object.values(workloadMap)
    .filter(w => w.todo + w.in_progress > 0)
    .sort((a, b) => (b.todo + b.in_progress) - (a.todo + a.in_progress))
    .slice(0, 12);
  const showWorkloadChart = workloadData.length > 0;

  // Tasks by Brand — Admins see dept-wide; Coordinator/Member see their own
  const brandMap = {};
  chartBase.forEach(t => {
    const b = t.brand || 'No Brand';
    if (b !== 'No Brand') brandMap[b] = (brandMap[b] || 0) + 1;
  });
  const brandData = Object.entries(brandMap).map(([name, value]) => ({ name, value }));
  const showBrandChart = brandData.length > 0 && (isAdmin || isSuperAdmin || isPersonalOnly);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {isPersonalOnly ? 'Your personal analytics' : isSuperAdmin ? 'Organization-wide analytics' : `${myDepartments.join(', ')} analytics`}
        </p>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="analytics-filters">
        {/* Hierarchical Date Filter: Today | Year → Month → Date */}
        <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-2" data-testid="date-filter-hierarchy">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          
          {/* Today quick button */}
          <button
            onClick={() => { const n = new Date(); setFilterYear(n.getFullYear()); setFilterMonth(n.getMonth()); setFilterDay(n.getDate()); }}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${isToday ? 'bg-[#F5A623] text-black shadow-sm' : 'bg-background border hover:bg-accent'}`}
            data-testid="today-filter-btn"
          >
            Today
          </button>

          <span className="text-muted-foreground text-xs">|</span>

          {/* Year */}
          <Select value={String(filterYear)} onValueChange={(v) => { setFilterYear(Number(v)); setFilterDay(0); }}>
            <SelectTrigger className="w-20 h-8 text-xs" data-testid="year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>

          <span className="text-muted-foreground text-xs">→</span>

          {/* Month */}
          <Select value={String(filterMonth)} onValueChange={(v) => { setFilterMonth(Number(v)); setFilterDay(0); }}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="month-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="-1">All Months</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Date — only show when a specific month is selected */}
          {filterMonth >= 0 && (
            <>
              <span className="text-muted-foreground text-xs">→</span>
              <Select value={String(filterDay)} onValueChange={(v) => setFilterDay(Number(v))}>
                <SelectTrigger className="w-24 h-8 text-xs" data-testid="day-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">All Days</SelectItem>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
                    <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {/* Employee Filter (Admin/Super Admin only) */}
        {(isHOD || isSuperAdmin) && (
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger className="w-48" data-testid="employee-filter">
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Department Filter (Super Admin only) */}
        {isSuperAdmin && (
          <Select value={selectedDept} onValueChange={(v) => { setSelectedDept(v === 'all' ? '' : v); setSelectedEmployee(''); }}>
            <SelectTrigger className="w-52" data-testid="analytics-dept-filter">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Brand Multi-Select Filter (Admin/Super Admin) */}
        {(isAdmin || isSuperAdmin) && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 gap-2 text-sm" data-testid="brand-filter">
                <Filter className="h-4 w-4" />
                {selectedBrands.length === 0 ? 'All Brands' : `${selectedBrands.length} Brand${selectedBrands.length === 1 ? '' : 's'}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="start">
              <div className="space-y-1">
                {BRANDS.map(brand => (
                  <label key={brand} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm">
                    <Checkbox
                      checked={selectedBrands.includes(brand)}
                      onCheckedChange={() => toggleBrand(brand)}
                      data-testid={`brand-check-${brand.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    {brand}
                  </label>
                ))}
                {selectedBrands.length > 0 && (
                  <Button variant="ghost" size="sm" className="w-full mt-1 text-xs text-muted-foreground" onClick={() => setSelectedBrands([])} data-testid="clear-brand-filter">
                    Clear selection
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-md">
          <Filter className="h-3 w-3 inline mr-1" />{getFilterLabel()}
          {selectedBrands.length > 0 && (
            <span className="ml-1.5">| {selectedBrands.join(', ')}</span>
          )}
        </div>

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-destructive gap-1" onClick={clearAllFilters} data-testid="clear-all-filters">
            <X className="h-3 w-3" />Clear Filters
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50" data-testid="kpi-total">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground font-medium">Total Tasks</p>
            <p className="text-2xl font-bold mt-1">{totalTasks}</p>
          </CardContent>
        </Card>
        <Card className="border-border/50" data-testid="kpi-completion">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground font-medium">Completion Rate</p>
            <p className="text-2xl font-bold mt-1 text-green-500">{completionRate}%</p>
          </CardContent>
        </Card>
        <Card className="border-border/50" data-testid="kpi-ontime">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground font-medium">On-Time Rate</p>
            <p className="text-2xl font-bold mt-1 text-blue-500">{onTimeRate}%</p>
          </CardContent>
        </Card>
        <Card className="border-border/50" data-testid="kpi-delay">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground font-medium">Delay Rate</p>
            <p className="text-2xl font-bold mt-1 text-red-500">{delayRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Employee Workload (replaces Status Distribution) */}
        {showWorkloadChart ? (
          <Card className="border-border/50" data-testid="workload-chart">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Employee Workload</span>
                <span className="text-xs font-normal text-muted-foreground">To Do + In Progress · Top {workloadData.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={workloadData} margin={{ bottom: 60 }}>
                    <defs>
                      <linearGradient id="todoGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#A78BFA" />
                        <stop offset="100%" stopColor="#5B21B6" />
                      </linearGradient>
                      <linearGradient id="ipGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FBBF24" />
                        <stop offset="100%" stopColor="#92400E" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                    <Bar dataKey="todo" stackId="w" fill="url(#todoGrad)" name="To Do" radius={[0,0,0,0]} />
                    <Bar dataKey="in_progress" stackId="w" fill="url(#ipGrad)" name="In Progress" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50 border-dashed" data-testid="workload-empty">
            <CardHeader><CardTitle className="text-lg">Employee Workload</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64 flex flex-col items-center justify-center text-center text-muted-foreground">
                <p className="text-sm">No active workload</p>
                <p className="text-xs mt-1">No tasks in To Do or In Progress for the current filters</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tasks by Category */}
        {categoryData.length > 0 && (
          <Card className="border-border/50">
            <CardHeader><CardTitle className="text-lg">Tasks by Category</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryData} layout="vertical" margin={{ left: 80 }}>
                    <defs>
                      <linearGradient id="catGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#60A5FA" />
                        <stop offset="100%" stopColor="#1E3A8A" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
                    <Tooltip />
                    <Bar dataKey="value" fill="url(#catGradient)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Task Creation Trend */}
        {trendData.length > 0 && (
          <Card className="border-border/50">
            <CardHeader><CardTitle className="text-lg">Task Creation Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="tasks" stroke="#7C3AED" strokeWidth={2.5} dot={{ fill: '#7C3AED', r: 4 }} name="Tasks Created" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tasks by Employee (Admin/Super Admin only) */}
        {!isPersonalOnly && employeeData.length > 0 && (
          <Card className="border-border/50">
            <CardHeader><CardTitle className="text-lg">Tasks by Employee</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={employeeData} layout="vertical" margin={{ left: 100 }}>
                    <defs>
                      <linearGradient id="empGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FCD34D" />
                        <stop offset="100%" stopColor="#B45309" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={95} />
                    <Tooltip />
                    <Bar dataKey="value" fill="url(#empGradient)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employee Workload chart moved to top grid (replaced Status Distribution) */}

        {/* Task Count by Brand */}
        {showBrandChart && (
          <Card className="border-border/50" data-testid="brand-chart">
            <CardHeader><CardTitle className="text-lg">Task Count by Brand</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={brandData} layout="vertical" margin={{ left: 100 }}>
                    <defs>
                      <linearGradient id="brandGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#C4B5FD" />
                        <stop offset="100%" stopColor="#5B21B6" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={95} />
                    <Tooltip />
                    <Bar dataKey="value" fill="url(#brandGradient)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Task Count by Status — Coordinator/Member (own tasks) */}
        {showStatusChart && (
          <Card className="border-border/50" data-testid="status-chart">
            <CardHeader><CardTitle className="text-lg">Task Count by Status</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {statusData.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.name] || '#9CA3AF'} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Individual vs Team Tasks — D&D only */}
        {(user?.department === 'Design & Development' || (isSuperAdmin && selectedDept === 'Design & Development')) && (workTypeData[0].value > 0 || workTypeData[1].value > 0) && (
          <Card className="border-border/50" data-testid="worktype-chart">
            <CardHeader><CardTitle className="text-lg">Individual vs Team Tasks</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={workTypeData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      <Cell fill="#3B82F6" />
                      <Cell fill="#F59E0B" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
